import { useState } from 'react'
import T from 'prop-types'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table'
import { ChevronUp, ChevronDown, ChevronsUpDown, UserPlus, Trash } from 'lucide-react'
import { FetchingMain, RejectedMain, LoadingDots } from '../../common/components'
import PencilIcon from '../../common/components/Icons/PencilIcon'
import SearchIcon from '../../common/components/Icons/SearchIcon'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/common/components/ui/table'
import { Button } from '@/common/components/ui/button'
import { cancelAllBookingsForEmployee } from '../../api/booking-service'

const columnHelper = createColumnHelper()

async function handleCancelAllBookings(employee) {
  const confirmMessage = `Are you sure you want to cancel ALL bookings for ${employee.fullName} (${employee.email})?`
  if (!window.confirm(confirmMessage)) return
  try {
    const response = await cancelAllBookingsForEmployee(employee.email)
    const count = response.count || 0
    alert(`Successfully cancelled ${count} booking${count !== 1 ? 's' : ''} for ${employee.fullName}`)
  } catch (error) {
    alert(`Failed to cancel bookings: ${error.message}`)
  }
}

function SortIcon({ sorted }) {
  if (sorted === 'asc') return <ChevronUp className="h-4 w-4" />
  if (sorted === 'desc') return <ChevronDown className="h-4 w-4" />
  return <ChevronsUpDown className="h-4 w-4 text-gray-400" />
}

SortIcon.propTypes = {
  sorted: T.oneOfType([T.string, T.bool]).isRequired,
}

function SortableHeader({ column, label }) {
  const sorted = column.getIsSorted()
  return (
    <button
      className="flex items-center gap-1 font-medium hover:text-[#007d8b] transition-colors"
      onClick={() => column.toggleSorting(sorted === 'asc')}
    >
      {label}
      <SortIcon sorted={sorted} />
    </button>
  )
}

SortableHeader.propTypes = {
  column: T.shape({
    getIsSorted: T.func.isRequired,
    toggleSorting: T.func.isRequired,
  }).isRequired,
  label: T.string.isRequired,
}

function NameHeader({ column }) {
  return <SortableHeader column={column} label="Name" />
}
NameHeader.propTypes = { column: T.object.isRequired }

function RoleHeader({ column }) {
  return <SortableHeader column={column} label="Role" />
}
RoleHeader.propTypes = { column: T.object.isRequired }

function EmailHeader({ column }) {
  return <SortableHeader column={column} label="Email" />
}
EmailHeader.propTypes = { column: T.object.isRequired }

function EditHeader() {
  return <div className="text-center">Edit</div>
}

function CancelBookingsHeader() {
  return <div className="text-center">Cancel All Bookings</div>
}

function RemoveHeader() {
  return <div className="text-center">Remove</div>
}

function EditCell({ row, table }) {
  return (
    <div className="flex justify-center">
      <button
        className="p-1 hover:text-[#007d8b] transition-colors"
        onClick={() => table.options.meta.onItemClick(row.original)}
        aria-label="Edit employee"
      >
        <PencilIcon />
      </button>
    </div>
  )
}

EditCell.propTypes = {
  row: T.shape({ original: T.object.isRequired }).isRequired,
  table: T.shape({
    options: T.shape({
      meta: T.shape({ onItemClick: T.func.isRequired }).isRequired,
    }).isRequired,
  }).isRequired,
}

function CancelBookingsCell({ row }) {
  return (
    <div className="flex justify-center">
      <button
        className="p-1 text-base font-bold text-[#d32f2f] hover:opacity-70 transition-opacity"
        onClick={() => handleCancelAllBookings(row.original)}
        aria-label="Cancel all bookings"
      >
        ✕
      </button>
    </div>
  )
}

CancelBookingsCell.propTypes = {
  row: T.shape({ original: T.object.isRequired }).isRequired,
}

function RemoveCell({ row, table }) {
  return (
    <div className="flex justify-center">
      <button
        className="p-1 hover:opacity-70 transition-opacity"
        onClick={() => table.options.meta.onRemoveClick(row.original)}
        aria-label="Remove employee"
      >
        <Trash />
      </button>
    </div>
  )
}

RemoveCell.propTypes = {
  row: T.shape({ original: T.object.isRequired }).isRequired,
  table: T.shape({
    options: T.shape({
      meta: T.shape({ onRemoveClick: T.func.isRequired }).isRequired,
    }).isRequired,
  }).isRequired,
}

const COLUMNS = [
  columnHelper.accessor('fullName', {
    header: NameHeader,
  }),
  columnHelper.accessor('team', {
    header: RoleHeader,
    cell: (info) => info.getValue() || 'user',
  }),
  columnHelper.accessor('email', {
    header: EmailHeader,
  }),
  columnHelper.display({
    id: 'editEmployee',
    header: EditHeader,
    cell: EditCell,
  }),
  columnHelper.display({
    id: 'cancelBookings',
    header: CancelBookingsHeader,
    cell: CancelBookingsCell,
  }),
  columnHelper.display({
    id: 'deleteEmployee',
    header: RemoveHeader,
    cell: RemoveCell,
  }),
]

function EmployeeList({ data: { status, error, employees }, onItemClick, onAddClick, onRemoveClick, onGroupReservation, onManageSeats }) {
  const [globalFilter, setGlobalFilter] = useState('')
  const [sorting, setSorting] = useState([])
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 13 })

  const table = useReactTable({
    data: employees ?? [],
    columns: COLUMNS,
    state: { sorting, globalFilter, pagination },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    meta: { onItemClick, onRemoveClick },
  })

  if (status.isLoading) return (
    <FetchingMain>
      <label>
        <LoadingDots length={5}>Loading</LoadingDots>
        <progress />
      </label>
    </FetchingMain>
  )

  if (status.isRejected) return (
    <RejectedMain>
      <p>Error fetching employee list: {error.response?.data ?? error.message}</p>
    </RejectedMain>
  )

  return (
    <div className="mt-[105px] pt-3 px-20">
      <div className="flex flex-row justify-between items-center mt-5 mb-5">
        <div className="flex gap-2">
          <Button variant="outline" onClick={onGroupReservation}>Group Reservation</Button>
          <Button variant="outline" onClick={onManageSeats}>Manage Seats</Button>
          <Button variant="outline" onClick={() => onAddClick(null)}>
            <UserPlus />
            Add employee
          </Button>
        </div>

        <div className="SearchBar">
          <div className="SearchInput">
            <input
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              type="text"
              placeholder="Search an employee"
              name="search form"
            />
            <div className="SearchBarIcon">
              <SearchIcon />
            </div>
          </div>
        </div>
      </div>

      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={COLUMNS.length} className="text-center text-gray-500 py-8">
                No employees found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <div className="flex items-center justify-between py-4">
        <span className="text-sm text-gray-500">
          Page {table.getState().pagination.pageIndex + 1} of {Math.max(table.getPageCount(), 1)}
          {' '}({table.getFilteredRowModel().rows.length} employees)
        </span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}

EmployeeList.propTypes = {
  data: T.shape({
    employees: T.array.isRequired,
    status: T.object.isRequired,
    error: T.object,
  }).isRequired,
  onItemClick: T.func.isRequired,
  onAddClick: T.func.isRequired,
  onRemoveClick: T.func.isRequired,
  onGroupReservation: T.func.isRequired,
  onManageSeats: T.func.isRequired,
}

export default EmployeeList
